import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pill, Pencil } from "lucide-react";
import {
  useReceitasPendentes, DavPendenteReceita,
} from "@/features/anvisa/hooks/useReceitaMedica";
import ReceitaDialog from "./ReceitaDialog";

/** Primeiro dia do mês corrente (YYYY-MM-DD). */
function primeiroDiaMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function hojeIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function ReceitasSngpc() {
  const [inicio, setInicio] = useState(primeiroDiaMes());
  const [fim, setFim] = useState(hojeIso());
  const [apenasPendentes, setApenasPendentes] = useState(false);
  const [davSelecionado, setDavSelecionado] = useState<DavPendenteReceita | null>(null);

  const { data = [], isLoading } = useReceitasPendentes(inicio, fim, apenasPendentes);

  const totais = useMemo(() => {
    const pend = data.filter((d) => !d.temReceita).length;
    return { total: data.length, pendentes: pend, comReceita: data.length - pend };
  }, [data]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Pill className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Receitas Médicas (SNGPC)</h1>
          <p className="text-muted-foreground">
            Vendas com medicamentos controlados/antimicrobianos. Anexe a receita para
            que a venda entre na transmissão SNGPC.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-card rounded-lg border p-4 flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label>Início</Label>
          <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className="w-44" />
        </div>
        <div className="space-y-1">
          <Label>Fim</Label>
          <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className="w-44" />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch checked={apenasPendentes} onCheckedChange={setApenasPendentes} />
          <Label>Apenas pendentes</Label>
        </div>
        <div className="ml-auto flex gap-2 pb-1 text-sm">
          <Badge variant="outline">Total: {totais.total}</Badge>
          <Badge variant="outline" className="text-amber-600 border-amber-300">Pendentes: {totais.pendentes}</Badge>
          <Badge variant="outline" className="text-emerald-600 border-emerald-300">Com receita: {totais.comReceita}</Badge>
        </div>
      </div>

      {/* Grid */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">DAV</TableHead>
              <TableHead className="w-[120px]">Emissão</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead className="w-[120px] text-right">Controlados</TableHead>
              <TableHead className="w-[120px] text-right">Valor</TableHead>
              <TableHead className="w-[130px]">Situação</TableHead>
              <TableHead className="w-[90px]">Receita</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            )}
            {!isLoading && data.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                Nenhuma venda com medicamentos controlados no período.
              </TableCell></TableRow>
            )}
            {data.map((d) => (
              <TableRow key={d.movDavId}>
                <TableCell className="font-mono">{d.numeroDav}</TableCell>
                <TableCell>{new Date(d.dtEmissao).toLocaleDateString("pt-BR")}</TableCell>
                <TableCell className="font-medium">{d.clienteNome || "—"}</TableCell>
                <TableCell className="text-right">{d.qtdItensControlados}</TableCell>
                <TableCell className="text-right font-mono">
                  {d.valorTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </TableCell>
                <TableCell>
                  {d.temReceita
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Com receita</Badge>
                    : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendente</Badge>}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => setDavSelecionado(d)} title="Editar receita">
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {davSelecionado && (
        <ReceitaDialog
          dav={davSelecionado}
          open={!!davSelecionado}
          onClose={() => setDavSelecionado(null)}
        />
      )}
    </div>
  );
}
